/**
 * Thin fetch wrapper:
 * - Sends credentials (cookies) on every request
 * - Echoes CSRF token from cookie into X-CSRF-Token header on state-changing requests
 * - Parses JSON, throws typed ApiError on non-2xx
 */
import type { ErrorResponse } from '@schwrz/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, body: ErrorResponse) {
    super(body.message ?? body.error);
    this.status = status;
    this.code = body.error;
    this.details = body.details;
  }
}

type WorkspaceUnverifiedHandler = () => void;
let workspaceUnverifiedHandler: WorkspaceUnverifiedHandler | null = null;

/** Register handler for suspended-workspace API responses. */
export function onWorkspaceUnverified(handler: WorkspaceUnverifiedHandler): void {
  workspaceUnverifiedHandler = handler;
}

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCsrfToken(): string | null {
  const match = /(?:^|;\s*)csrf-token=([^;]+)/.exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export interface ApiRequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Survives page unload — use only for fire-and-forget requests (no response parsing). */
  keepalive?: boolean;
}

function parseResponseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function apiErrorFromResponse(status: number, data: unknown): ApiError {
  const errBody = (data ?? {}) as Partial<ErrorResponse>;
  const code = errBody.error ?? (status >= 500 ? 'internal' : 'unknown');
  const message =
    errBody.message ??
    (status >= 500 ? 'Internal server error' : code === 'unknown' ? 'Request failed' : code);
  return new ApiError(status, { error: code, message, details: errBody.details });
}

function handleApiError(err: ApiError): void {
  if (err.status === 403 && err.code === 'workspace_unverified') {
    workspaceUnverifiedHandler?.();
  }
}

export async function api<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...opts.headers,
  };
  if (STATE_CHANGING.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    keepalive: opts.keepalive ?? false,
  });

  const text = await res.text();
  const data = parseResponseJson(text);

  if (!res.ok) {
    const err = apiErrorFromResponse(res.status, data);
    handleApiError(err);
    throw err;
  }
  return data as T;
}

/** Fire-and-forget DELETE that survives tab unload (no response handling). */
export function apiDeleteKeepalive(path: string): void {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const csrf = readCsrfToken();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  void fetch(path, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers,
    keepalive: true,
  });
}

export async function apiFormData<T>(
  path: string,
  formData: FormData,
  opts: { method?: string; signal?: AbortSignal } = {},
): Promise<T> {
  const method = (opts.method ?? 'POST').toUpperCase();
  const headers: Record<string, string> = { Accept: 'application/json' };
  const csrf = readCsrfToken();
  if (csrf) headers['X-CSRF-Token'] = csrf;

  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    body: formData,
    signal: opts.signal,
  });

  const text = await res.text();
  const data = parseResponseJson(text);

  if (!res.ok) {
    const err = apiErrorFromResponse(res.status, data);
    handleApiError(err);
    throw err;
  }
  return data as T;
}
